/**
 * What Sneakers says about an answer, and at the end of a lesson.
 *
 * It used to be one line each — *"Yes! That is right 🎉"* after every right
 * answer, *"So close! 💛"* after every miss, the same goodbye after every
 * lesson — and a child hears those dozens of times a day. Praise that never
 * changes stops being praise and becomes a sound the app makes.
 *
 * So these are pools, hundreds of lines between them, and a line is drawn at
 * random with the last few kept out so the same one never comes twice in a
 * row. The rules every line keeps:
 *
 * - **Simple English.** The app speaks it in every course (D-068). Lines that
 *   name the language say `{language}`, which `pick` fills in (D-190). Short words, short
 *   sentences, nothing a nine-year-old in Denmark would need to look up to
 *   feel praised.
 * - **A miss is never called wrong** ([Mascot](../../../docs/MASCOT.md)). The
 *   close lines are warm, and about trying again, never about failing.
 * - **Says which it was in words.** The two feedback panels are close in
 *   greyscale on purpose, so a line must not read the same either way: every
 *   right line is plainly a yes, and every close line plainly a not-yet.
 * - **About the child's work, not the child's worth.** "You worked that out",
 *   not "you are a genius" — praise for effort is the kind that keeps a child
 *   trying when it gets hard.
 */

import { learningNow } from './language.js';

/** Right answers. */
export const CORRECT: readonly string[] = [
  'Yes! That is right 🎉',
  'Spot on! 🎯',
  'You got it! ⭐',
  'Brilliant! ✨',
  'Perfect! 🌟',
  'Yes, yes, yes! 🎉',
  'Nailed it! 🔨',
  'Exactly right! 👏',
  'Super! 🚀',
  'Great work! 💪',
  'Well done! 🏅',
  'That is it! 🙌',
  'Correct! 🎊',
  'Wonderful! 🌈',
  'Fantastic! 🤩',
  'Awesome! 😎',
  'You are on a roll! 🎳',
  'Right on! 👍',
  'Bingo! 🎱',
  'Top marks! 🥇',
  'Look at you go! 🏃',
  'Yes! You know this one! 🧠',
  'Amazing! 🌟',
  'Ding ding ding! 🔔',
  'Great job! 🎈',
  'High five! ✋',
  'Wow, nice! 😍',
  'That is the one! ✅',
  'Excellent! 🏆',
  'Sneakers is proud! 🐹',
  'Lovely! 🌸',
  'Splendid! 🎩',
  'Ace! 🃏',
  'You did it! 🎉',
  'Smashing! 💥',
  'Cool! 🧊',
  'So good! 💫',
  'Yes, that is right! 🙂',
  'Perfect answer! 💯',
  'Good thinking! 💡',
  'You worked it out! 🧩',
  'Clever! 🦉',
  'Well spotted! 👀',
  'Good ears! 👂',
  'Good eyes! 👀',
  'You remembered! 🧠',
  'Right again! 🔁',
  'Yes! Keep it up! ⬆️',
  'Easy peasy! 🍋',
  'Like a pro! 😎',
  'That was quick! ⚡',
  'Super star! ⭐',
  'Hooray! 🎉',
  'Yippee! 🥳',
  'Woohoo! 🎊',
  'Boom! 💥',
  'Yes indeed! 👌',
  'Absolutely right! ✔️',
  'You are getting good at this! 📈',
  'That is how it is done! 🎬',
  'Magic! 🪄',
  'Fab! 🌟',
  'Terrific! 🎆',
  'Marvellous! 🎠',
  'Top job! 🏅',
  'Way to go! 🛤️',
  'You are flying! 🦅',
  'Look at that! 👏',
  'Right first time! 🥇',
  'Gold star for you! ⭐',
  'Sharp as a pencil! ✏️',
  'Nice work! 🛠️',
  'Nice one! 👍',
  'That is right — well done! 🎉',
  'Your {language} is growing! 🌱',
  'That was a tricky one, and you got it! 🧗',
  'Right! Keep going like this! 🚂',
  'You make it look easy! 😊',
  'Superb! 🎻',
  'Outstanding! 🌄',
  'Bravo! 👏',
  'Yay! 🎈',
  'Ta-da! 🎩',
  'Click! It fits! 🧩',
  'Right as rain! 🌦️',
  'Sweet! 🍭',
  'Wicked! 🤘',
  'Spot-on {language}! 🗣️',
  'That is perfect {language}! 📚',
  'Right! Your brain is working hard! 🧠',
  'You know your stuff! 🎓',
  'Sneakers does a happy dance! 💃',
  'Another one right! ➕',
  'Score! ⚽',
  'Goal! 🥅',
  'Slam dunk! 🏀',
  'Home run! ⚾',
  'Hole in one! ⛳',
  'Bullseye! 🎯',
  'Straight to the top! 🧗',
  'You are a word wizard! 🧙',
  'Shiny! ✨',
  'Gorgeous! 🌺',
  'Lovely job! 💖',
  'That is right, my friend! 🤝',
  'Correct-a-mundo! 😄',
  'You got it in one! ☝️',
  'Strong work! 🏋️',
  'Rock solid! 🪨',
  'Super smart thinking! 💭',
  'I knew you could! 💛',
  'Great listening! 🎧',
  'Great reading! 📖',
  'Fantastic work! 🌟',
  'Really well done! 🏅',
  'That is it exactly! 🎯',
  'Wow! You are fast! 🏎️',
  'Nice and easy! 🌤️',
  'You are a star! 🌟',
  'Cracked it! 🥚',
  'Sorted! 📦',
  'Done and done! ✅',
  'Yes — keep going! ▶️',
  'Neat! 🧼',
  'Tip-top! 🎩',
  'First class! 🚄',
  'Super duper! 🦸',
  'Hip hip hooray! 🎉',
  'Totally right! 💯',
  'Your hard work shows! 💪',
  'That was great! 🎶',
  'A perfect fit! 🧦',
  'Right you are! 👌',
  'So clever! 🦊',
  'Champion! 🏆',
  'You are learning so fast! 🚀',
  'Look how much you know! 📚',
  'Yes! Onwards! 🧭',
  'That deserves a cheer! 📣',
  'Hurray for you! 🎊',
  'Lovely {language}! 🌷',
  'Perfecto! 👌',
  'Class! 🎓',
  'Sneakers gives you a thumbs up! 👍',
  'Smooth! 🧈',
  'Fantastic listening! 🎵',
  'That was the right one! ✔️',
];

/** A clear miss. Warm, and about having another go. */
export const CLOSE: readonly string[] = [
  'So close! 💛',
  'Nearly! 🌱',
  'Almost there! 🧗',
  'Good try! 💪',
  'Not quite — you will get it! 🌈',
  'Ooh, close one! 🤏',
  'Nice try! 🙂',
  'That one is tricky! 🧩',
  'Good effort — nearly there! 🌟',
  'Keep going, you are learning! 📚',
  'Almost! Let us look at it together. 👀',
  'That is a hard one! 🧠',
  'Not this time — next time! ⏭️',
  'You are getting closer! 🎯',
  'Good thinking, nearly there! 💡',
  'Every try helps you learn! 🌱',
  'Mistakes help us grow! 🌻',
  'Oops! That happens to everyone. 💛',
  'Hmm, not quite! 🤔',
  'Close, but not quite! 🤏',
  'That was a brave try! 🦁',
  'Keep trying — you can do it! 💪',
  'So nearly! 🌟',
  'Almost had it! ✋',
  'You will know it next time! 🔁',
  'Let us remember this one! 🧠',
  'Good go — nearly! 🙌',
  'Not to worry! 💛',
  'No problem — let us learn it! 📖',
  'That is OK! Look at the answer. 👀',
  'Tricky! Now you know. 💡',
  'Close! Have a look. 🔍',
  'Good guess! 🎲',
  'Almost right! 🌤️',
  'Nearly, nearly! 🐢',
  'Ahh, so close! 💛',
  'You were close! 🎯',
  'Not yet — keep at it! 🔨',
  'Not quite — learning takes practice! 🎹',
  'Try, try again! 🔁',
  'One more step! 👣',
  'That is how we learn! 🌱',
  'Sneakers thinks you nearly had it! 🐹',
  'Good attempt — not quite! 👍',
  'Brave try! 🛡️',
  'You will get the next one! ⭐',
  'Not yet — do not give up! 🚀',
  'Even pros miss sometimes! 🏀',
  'Almost! You are doing well. 🌈',
  'It is OK — everyone learns! 🤗',
  'Close enough to learn from! 📚',
  'Nice try, friend! 🤝',
  'Just a little bit off! 🤏',
  'Hmm, that one is sneaky! 🦊',
  'Not quite — but good thinking! 💭',
  'You are still learning, and that is great! 🌻',
  'Next time for sure! ✨',
  'A tricky one! Let us look. 👓',
  'Not quite — keep your head up! ☀️',
  'Not this one — shake it off! 🐶',
  'Nearly there, superstar! ⭐',
  'Good ears — almost! 👂',
  'Ooh, nearly! 🌟',
  'Not yet — but soon! ⏳',
  'Every mistake is a lesson! 🎓',
  'So close I can taste it! 🍪',
  'Almost got it! 🧲',
  'Little slip! 🍌',
  'Not quite right, but well tried! 💛',
  'Let us try that one again soon! 🔁',
];

/** The end of a lesson that went well. */
export const FINISHED_WELL: readonly string[] = [
  'Nicely done! You know these words now.',
  'What a lesson! You did brilliantly.',
  'Wow! You really know your stuff.',
  'Great work today! Your {language} is growing.',
  'That was fantastic! See you next time.',
  'You smashed it! I am so proud of you.',
  'Brilliant! These words are yours now.',
  'Super job! You made that look easy.',
  'Amazing work! You are a word wizard.',
  'You did it! And you did it well.',
  'Top marks! That was a great lesson.',
  'Hooray! Another lesson done, and done well.',
  'Look at you! So many right answers.',
  'Excellent! Your hard work is paying off.',
  'That was wonderful. Give yourself a high five!',
  'You are getting so good at {language}!',
  'Fantastic! I think you are ready for more.',
  'Great listening and great thinking!',
  'You were on fire! Well, a nice warm fire.',
  'Splendid! I will tell all the other capybaras.',
  'Perfect! Well, nearly perfect. Very good!',
  'You learned a lot today. Well done!',
  'Wow, you are quick! Great lesson.',
  'That was a super lesson. You should be proud.',
  'Yes! Another step up the path.',
  'Lovely work. Your brain got stronger today.',
  'Brilliant! Time for a little dance.',
  'You make teaching easy! Well done.',
  'That was great fun. Let us do more soon!',
  'Superb! You remembered so much.',
  'You are a star! A shiny {language} star.',
  'Well done! These words will stick now.',
  'Great job! I could not have done better.',
  'Awesome! You kept going all the way.',
  'Marvellous! What a clever learner you are.',
  'You crushed it! Like a nut. Mmm, nuts.',
  'Cool! That lesson was easy for you.',
  'Excellent listening! Your ears are amazing.',
  'Wonderful! You are really learning fast.',
  'Yay! You finished, and you did so well.',
  'A great lesson! You should feel proud.',
  'Look how far you have come!',
  'Nice! Your {language} gets better every day.',
  'That was a winner! Well done.',
  'You did really well. I am so happy!',
  'Bravo! A lesson to remember.',
  'Woohoo! You were great today.',
  'Champion work! You earned that.',
  'That was impressive! Keep it up.',
  'So many right answers! Brilliant.',
];

/** The end of a lesson that was hard going. Warm, and never about a loss. */
export const FINISHED_GENTLY: readonly string[] = [
  'Good going. We will practise them again soon.',
  'You finished! That is the most important part.',
  'Well done for keeping going. That was tricky!',
  'Good work! These words will come back, and you will know them.',
  'You did it! Hard lessons make you stronger.',
  'Nice effort! We will see these words again.',
  'You kept trying, and that is what matters.',
  'That was a tough one. I am proud you finished it!',
  'Good job! Practice makes progress.',
  'Every lesson helps. Well done today!',
  'Great effort! Next time it will be easier.',
  'You stuck with it. That is brilliant!',
  'Tricky words! We will practise them together.',
  'You finished a hard lesson. High five!',
  'Not easy, but you did it! Well done.',
  'Good work. Little by little, you are learning.',
  'Well done! These words will feel easier soon.',
  'Thanks for trying so hard today!',
  'A good lesson! Your brain worked hard.',
  'You did your best, and that is great.',
  'Keep going like this and you will be amazing!',
  'Good try today! We will look at these again.',
  'Learning is a journey. You took a step today!',
  'Finished! Have a rest — you earned it.',
  'Great effort! Every try helps you learn.',
  'You are braver than a lion. Well done!',
  'That was hard, and you did not give up!',
  'Well done! I will bring these words back soon.',
  'Good going! Tomorrow these will feel easier.',
  'You finished! I am happy you kept going.',
  'Nice work. We will practise the tricky ones.',
  'Every lesson makes you better. Good job!',
  'Strong finish! Hard words are worth learning.',
  'Good job today. Let us try again soon!',
  'You did well to finish. Keep it up!',
  'Hard work today! Your {language} is growing.',
  'Well done! Mistakes are how we learn.',
  'Good lesson! We will meet these words again.',
  'You kept going — that is what learners do!',
  'Lovely effort. I am proud of you.',
];

/**
 * A line from a pool, never one of the last few.
 *
 * Module state rather than component state, because a lesson remounts its
 * feedback panel for every question and the memory has to outlive it.
 */
const recent = new Map<readonly string[], string[]>();

/** How many recent lines a pool keeps out. */
const MEMORY = 8;

export function pick(
  pool: readonly string[],
  random: () => number = Math.random,
): string {
  const kept = recent.get(pool) ?? [];
  const fresh = pool.filter((line) => !kept.includes(line));
  const from = fresh.length > 0 ? fresh : pool;
  const line = from[Math.floor(random() * from.length)] ?? pool[0] ?? '';
  // Never all of a pool, or nothing would be left to say.
  const keep = Math.min(MEMORY, pool.length - 1);
  recent.set(pool, keep <= 0 ? [] : [...kept, line].slice(-keep));
  return fill(line);
}

/** A line as it is said: *Your Swedish is growing!* for a child learning Swedish (D-190). */
export const fill = (line: string): string =>
  line.replaceAll('{language}', learningNow().title);

/** A pool as a child would see it now, every line filled in. */
export const said = (pool: readonly string[]): readonly string[] => pool.map(fill);
